import dayjs from 'dayjs'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import ModeSwitch from '../../../../components/ModeSwitch'
import { defaultVat } from '../../../../constants'
import useUser from '../../../../contexts/hooks/useUser'
import { trpc } from '../../../../modules/trpc'
import PageWrapper from '../../../../wrappers/pageWrapper/PageWrapper'

const ElectricCarChargingHistory = () => {
	const { vehicleId } = useParams<{ vehicleId: string }>()

	const { user } = useUser()

	const [currentTime, setCurrentTime] = useState('month')

	const { t } = useTranslation('pages_electricCar')

	const timeOptions = [
		{ mode: 'week', label: t('chargingStatistics.timeSwitcher.week') },
		{ mode: 'month', label: t('chargingStatistics.timeSwitcher.month') },
		{ mode: 'year', label: t('chargingStatistics.timeSwitcher.year') },
		{ mode: 'all', label: t('chargingStatistics.timeSwitcher.all') },
	]

	const getStartDate = (timePeriod: string) => {
		switch (timePeriod) {
			case 'week':
				return dayjs().subtract(1, 'week').format('YYYY-MM-DD')
			case 'year':
				return dayjs().subtract(1, 'year').format('YYYY-MM-DD')
			case 'all':
				return dayjs('2020-01-01').format('YYYY-MM-DD') // Arbitrary early date for "all"
			case 'month':
			default:
				return dayjs().subtract(1, 'month').format('YYYY-MM-DD')
		}
	}

	const { data: smartChargingStats } =
		trpc.eNode.statistics.getUserStatisticsOnChargingSessions.useQuery({
			hashedPid: user?.hashedPid ?? '',
			options: {
				startDate: getStartDate(currentTime),
				type: 'vehicle',
				id: vehicleId,
			},
		})
	const sortedStats = smartChargingStats?.slice().sort((a, b) => dayjs(b.from).diff(dayjs(a.from)))

	return (
		<PageWrapper
			page='electricCarChargingHistory'
			appBarOptions={{
				label: t('chargingStatistics.title'),
				navigationType: 'back',
			}}
		>
			<ModeSwitch
				options={timeOptions}
				currentMode={currentTime}
				onModeChange={setCurrentTime}
				border
			/>
			<div className='container'>
				<div className='summary dark:bg-brand-secondary-200-dark'>
					<div className='summary-item text-text-dark dark:text-white dark:bg-background-200-dark border dark:border-brand-secondary-100-dark bg-white border-brand-secondary-100-light'>
						<p>{t('chargingStatistics.totalCharges')}</p>
						<p className='dark:text-brand-primary-100-dark'>
							{smartChargingStats?.length} {t('chargingStatistics.times')}
						</p>
					</div>
					<div className='summary-item text-text-dark dark:text-white dark:bg-background-200-dark border dark:border-brand-secondary-100-dark bg-white border-brand-secondary-100-light'>
						<p>{t('chargingStatistics.totalSaves')}</p>
						<p className='dark:text-brand-primary-100-dark'>
							{smartChargingStats
								?.reduce((acc, stat) => acc + (stat.estimatedSavings ?? 0) * (1 + defaultVat), 0)
								.toFixed(2)}
							€
						</p>
					</div>
				</div>

				{sortedStats?.map((stat, index) => (
					<div
						key={index}
						className='transaction bg-white dark:bg-brand-secondary-200-dark text-text-dark dark:text-white border border-brand-secondary-100-light dark:border-brand-secondary-100-dark'
					>
						<div className='time'>{Math.abs(dayjs(stat.to).diff(dayjs(stat.from), 'hour'))}h</div>
						<div className='cost-section'>
							<div className='cost'>
								<div className='price'>{(stat.costSum * (1 + defaultVat)).toFixed(2)}€</div>
								<div className='kwh'>{stat.kwhSum} kWh</div>
							</div>
							<div className='timestampHour text-grey-200 dark:text-text-grey-100'>
								{dayjs(stat.from).format('DD.MM.YYYY')} | {dayjs(stat.from).format('HH:mm')} -{' '}
								{dayjs(stat.to).format('HH:mm')}
							</div>
						</div>

						<div className='saved'>
							{(stat.estimatedSavings ? stat.estimatedSavings * (1 + defaultVat) : 0).toFixed(2)}€{' '}
							{t('chargingStatistics.saves')}
						</div>
					</div>
				))}
			</div>
		</PageWrapper>
	)
}
export default ElectricCarChargingHistory
